import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import type {
  ProductTypeSpecRule,
  SpecRuleCondition,
  SpecRuleOutcome,
  SpecRuleOperator,
} from 'api/types/product-type/ProductTypeSpecRule';
import { normalizeTurkishCompareKey } from 'utils/specConstraintUtils';

export interface SpecRuleEvaluationEntry {
  /** Whether the spec input should be rendered at all (gated specs stay hidden until controlled). */
  visible: boolean;
  /** Effective enum options (subset of the spec's allowed values, original order preserved). */
  allowedValues: string[];
  /** Optional numeric bounds derived from comparison outcomes. */
  min?: number;
  max?: number;
}

export type SpecRuleEvaluation = Record<string, SpecRuleEvaluationEntry>;

export interface EvaluateSpecRulesParams {
  constraints: SpecConstraintViewModel[];
  rules: ProductTypeSpecRule[] | null | undefined;
  /** Single-value selections keyed by spec key. */
  values: Record<string, string>;
  /** Multi-value selections keyed by spec key (browse filters). */
  multiValues?: Record<string, string[]>;
}

/**
 * Evaluate the conditional rule set for a product type against the currently selected spec values.
 * Returns a per-spec-key map describing visibility and the effective allowed values.
 *
 * Semantics:
 * - A spec that is never the target ("outcome") of any rule is always visible with its full option list.
 * - A spec that is a target is gated: it becomes visible only once at least one rule targeting it has a
 *   satisfied condition (product-type conditions are always satisfied, so they keep the spec always visible).
 * - Effective allowed values = union of the outcome value sets of all satisfied rules targeting the spec,
 *   intersected with the spec's base allowed values (keeping original order).
 */
export function evaluateSpecRules({
  constraints,
  rules,
  values,
  multiValues,
}: EvaluateSpecRulesParams): SpecRuleEvaluation {
  const evaluation: SpecRuleEvaluation = {};
  const ruleList = rules ?? [];

  const getSelectedValues = (specKey: string): string[] => {
    const result: string[] = [];
    const multi = multiValues?.[specKey];
    if (multi && multi.length > 0) result.push(...multi);
    const single = values[specKey];
    if (single != null && String(single).trim() !== '') result.push(String(single));
    return result;
  };

  const conditionSatisfied = (condition: SpecRuleCondition | undefined): boolean => {
    if (!condition) return false;
    if (condition.type === 'productType') return true;
    if (!condition.specKey) return false;

    const selected = getSelectedValues(condition.specKey);
    if (selected.length === 0) return false;

    return selected.some((sel) => compare(sel, condition.operator, condition.value, condition.values));
  };

  for (const constraint of constraints) {
    const key = constraint.key;
    const baseAllowed = (constraint.allowedValues ?? []).map((av) => av.value);

    const targetingRules = ruleList.filter(
      (r) => r.outcome && normalizeTurkishCompareKey(r.outcome.specKey) === normalizeTurkishCompareKey(key)
    );

    if (targetingRules.length === 0) {
      evaluation[key] = { visible: true, allowedValues: baseAllowed };
      continue;
    }

    const hasProductTypeRule = targetingRules.some((r) => r.condition?.type === 'productType');
    const satisfiedRules = targetingRules.filter((r) => conditionSatisfied(r.condition));

    const visible = hasProductTypeRule || satisfiedRules.length > 0;

    if (satisfiedRules.length === 0) {
      // Gated & not yet unlocked → keep base options for when it becomes visible (it won't render while hidden).
      evaluation[key] = { visible, allowedValues: baseAllowed };
      continue;
    }

    const { allowedValues, min, max } = combineOutcomes(satisfiedRules.map((r) => r.outcome), baseAllowed);
    evaluation[key] = { visible, allowedValues, min, max };
  }

  return evaluation;
}

function combineOutcomes(
  outcomes: (SpecRuleOutcome | undefined)[],
  baseAllowed: string[]
): { allowedValues: string[]; min?: number; max?: number } {
  const allowedUnion = new Set<string>();
  let hasEnumConstraint = false;
  let min: number | undefined;
  let max: number | undefined;

  for (const outcome of outcomes) {
    if (!outcome) continue;
    const op = outcome.operator;

    if (op === 'in' || op === '=') {
      hasEnumConstraint = true;
      const list = outcome.values && outcome.values.length > 0
        ? outcome.values
        : outcome.value != null && outcome.value !== ''
          ? [outcome.value]
          : [];
      list.forEach((v) => allowedUnion.add(normalizeTurkishCompareKey(v)));
    } else if (op === '>' || op === '>=' || op === '<' || op === '<=') {
      const bound = toNumber(outcome.value);
      if (bound != null) {
        if (op === '>' || op === '>=') min = min == null ? bound : Math.min(min, bound);
        if (op === '<' || op === '<=') max = max == null ? bound : Math.max(max, bound);
      }
    } else if (op === '!=') {
      // Exclusion narrows the enum list.
      hasEnumConstraint = true;
      const excluded = normalizeTurkishCompareKey(outcome.value ?? '');
      baseAllowed.forEach((v) => {
        if (normalizeTurkishCompareKey(v) !== excluded) allowedUnion.add(normalizeTurkishCompareKey(v));
      });
    }
  }

  const allowedValues = hasEnumConstraint
    ? baseAllowed.filter((v) => allowedUnion.has(normalizeTurkishCompareKey(v)))
    : baseAllowed;

  return { allowedValues, min, max };
}

function compare(
  actual: string,
  operator: SpecRuleOperator,
  value: string | null | undefined,
  values: string[] | null | undefined
): boolean {
  switch (operator) {
    case 'in':
      return (values ?? []).some((v) => stringEquals(v, actual));
    case '=':
      return values && values.length > 0
        ? values.some((v) => stringEquals(v, actual))
        : stringEquals(value, actual);
    case '!=':
      return !stringEquals(value, actual);
    case '>':
    case '<':
    case '>=':
    case '<=':
      return compareNumeric(actual, operator, value);
    default:
      return stringEquals(value, actual);
  }
}

function compareNumeric(actual: string, operator: SpecRuleOperator, value: string | null | undefined): boolean {
  const a = toNumber(actual);
  const b = toNumber(value);
  if (a == null || b == null) return false;
  switch (operator) {
    case '>':
      return a > b;
    case '<':
      return a < b;
    case '>=':
      return a >= b;
    case '<=':
      return a <= b;
    default:
      return false;
  }
}

function toNumber(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

function stringEquals(a: string | null | undefined, b: string | null | undefined): boolean {
  return normalizeTurkishCompareKey(a ?? '') === normalizeTurkishCompareKey(b ?? '');
}
